import { useState, useEffect, useRef, useCallback } from 'react'
import { Calendar, Clock, Video, MapPin, Pill, FileText, PenTool, Check, CheckCircle, Send, ChevronDown, ChevronLeft, User, Building, Shield, Phone, X, Edit3, Star, ArrowLeft, Eye, Save, AlertTriangle } from 'lucide-react'
import { OrellaChatBubble, ChatKeyframes } from './OrellaChatHeader'
import PharmacyLookup from './PharmacyLookup'
import AppointmentCalendar from './AppointmentCalendar'
import NoSlotsCard from './NoSlotsCard'
import { datesOf, timesForDate, normalizeSlots, timesAfterSameDayLead, slotPlace, slotPlaceNames, isTimeRequestReason, requestGridSlots } from '../utils/bookableSlots'
import SlotPlaceLine from './SlotPlaceLine'
import AgreementPreviewModal from './AgreementPreviewModal'
import { PatientUploadSection } from '../pages/case/AuthorizationsScreen'
import { formatDate } from '../utils/dateUtils'
import { formatAddress } from '../utils/formatAddress'
import { useProfileStore } from '../stores'
import { useI18n } from '../i18n'
import { allowedVisitTypesForDoctor } from '../utils/offeredModalities' // PROD-644
import { clinicianName } from '../utils/clinicianName.js'
import { OrellaSpinner } from './OrellaLoader' // s123: the app's one spinner

const NAVY = '#15354F'
const BLUE = '#46A8F9'
const ICE = '#E9F8FF'

/**
 * ShareConversation v118.2.1 — Stack + Transition Hybrid
 * 
 * STACK = completed card stays visible, new content appears below
 * TRANSITION = appointment sub-steps replace each other, then final selection stacks
 * 
 * Flow:
 *   intro (stack) → appointment→visit-format→date-time (transition group, then stack) 
 *   → pharmacy (stack) → agreement (stack) → review (stack)
 */

// Phases that stack as completed cards
const STACK_PHASES = ['intro', 'appointment-group', 'pharmacy', 'agreement', 'review']

// Sub-steps within the appointment transition group
// v177.0.0: Removed 'appointment' yes/no step — scheduling is mandatory
const APPT_STEPS = ['visit-format', 'date-time']

export default function ShareConversation({
  doctor,
  currentCase,
  availableSlots,
  slotPlaces = null,
  loadingSlots,
  slotsReason = null,
  onRetrySlots,
  savedPharmacy,
  agreementSigned,
  signature,
  onRequestAppointment,
  onSelectPharmacy,
  onSignAgreement,
  onSend,
  onBack,
  onViewProfile,
  // TEST 3.15 (patient push 3a): told the current step, so the profile sheet's CTA can name it
  onStepChange,
  sendingToDoctor,
  mdOnboardMode,
  existingAppointment,
  authRequired = false,
  // P1 round 6 (A1): the refusal ShareScreen's send set (it was rendered nowhere on this
  // step), and a counter it bumps when the send came back as a slot conflict.
  error = '',
  slotConflictSeq = 0,
  // s106 (canvas "Orella Referrals Screens" A2): the patient chose "ask my PCP for a referral" (or will upload
  //   one). The specialist gets the case now and booking stays locked until the referral is on file
  //   (decision 1; the API's _referral_lock_refusal), so the appointment step is skipped and the review says so.
  holdBooking = false,
}) {
  // Stack: completed items displayed as cards
  const { t } = useI18n()
  const [showAgreementPreview, setShowAgreementPreview] = useState(false)
  const [stack, setStack] = useState([]) // [{phase, data}]
  
  // Active phase + sub-step for appointment transitions
  const [activePhase, setActivePhase] = useState('intro')
  const [apptStep, setApptStep] = useState('visit-format') // within transition group
  // v235.1.0 — auth-required patients get an authorization step between
  // visit-format and date-time. Order is derived so back-nav + advance agree.
  const apptStepOrder = authRequired ? ['visit-format', 'authorization', 'date-time'] : APPT_STEPS
  const [showContent, setShowContent] = useState(false)
  
  // Appointment state — v177.0.0: always true, scheduling is mandatory
  const [wantsAppointment, setWantsAppointment] = useState(true)
  // v189.2 BT-12: Default to doctor's accepted modality — don't suggest video if MD doesn't accept
  const doctorAcceptsVideo = (() => {
    if (!doctor) return true // fallback: show both options
    const types = doctor.appointment_types || []
    const at = doctor.appointmentTypes || {}
    if (types.some(t => t?.includes?.('virtual') || t?.includes?.('video') || t?.includes?.('telehealth'))) return true
    if (at.newVirtual || at.followupVirtual) return true
    // If doctor has no appointment_types set at all, default to showing both
    if (types.length === 0 && Object.keys(at).length === 0) return true
    return false
  })()
  // ★★ PROD-644 (patient P2): only the modalities this physician OFFERS for a first visit (the md10a
  //   rule, utils/offeredModalities). When the doctor payload does not say, today's behaviour stays:
  //   doctorAcceptsVideo above, in-person always.
  const offeredFirstVisit = allowedVisitTypesForDoctor({ doctor, category: 'initial', videoAllowedForCategory: true })
  const offersVideo = offeredFirstVisit.restricted ? offeredFirstVisit.types.includes('video') : doctorAcceptsVideo
  const offersInPerson = offeredFirstVisit.restricted ? offeredFirstVisit.types.includes('in_person') : true
  const [appointmentType, setAppointmentType] = useState(offersVideo ? 'video' : 'in-person')
  const [selectedDate, setSelectedDate] = useState(existingAppointment?.date || null)
  const [selectedTime, setSelectedTime] = useState(existingAppointment?.time || null)
  
  // Pharmacy state
  const [pharmacy, setPharmacy] = useState(savedPharmacy || null)
  const [pendingPharmacy, setPendingPharmacy] = useState(null) // awaiting save-choice
  const syncSection = useProfileStore((s) => s.syncSection)
  const updateProfile = useProfileStore((s) => s.updateProfile)
  // v235.7.2 — self-pay patients see the doctor's cash rate on each visit-format
  // option (virtual vs in-office) so price is visible at the moment they choose.
  const _insurance = useProfileStore((s) => s.profile?.insurance)
  const isSelfPayPatient = !!_insurance?.selfPay
  const _spInOffice = doctor?.self_pay_in_office_rate
  const _spVirtual = doctor?.self_pay_virtual_rate
  const spInOfficeOk = _spInOffice != null && !isNaN(Number(_spInOffice))
  const spVirtualOk = _spVirtual != null && !isNaN(Number(_spVirtual))

  // v158.2.0: Sync pharmacy when savedPharmacy prop arrives after initial mount (profile loads async)
  useEffect(() => {
    if (savedPharmacy && !pharmacy) {
      setPharmacy(savedPharmacy)
    }
  }, [savedPharmacy])
  
  const scrollRef = useRef(null)
  const drName = clinicianName(doctor, 'short')
  const drFullName = clinicianName(doctor)

  // Reset content animation on phase/step change
  useEffect(() => { setShowContent(false) }, [activePhase, apptStep])
  // ★ TEST 3.15 (patient push 3a): the parent learns the pending step. The chosen physician's
  //   profile sheet names it and opens it (utils/shareStepCta.js); it used to say "Continue"
  //   and only close itself.
  useEffect(() => { onStepChange?.({ activePhase, apptStep }) }, [activePhase, apptStep])

  // Auto-scroll — show top of new content (not bottom)
  useEffect(() => {
    if (showContent && scrollRef.current) {
      setTimeout(() => scrollRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 250)
    }
  }, [showContent, activePhase, apptStep, stack.length])

  // ─── Stack intro immediately on mount ───
  useEffect(() => {
    if (stack.length === 0 && activePhase === 'intro') {
      // Step 1: Show intro bubble + doctor card after initial typing animation
      setTimeout(() => {
        setStack([{ phase: 'intro' }])
      }, 1800)
      // Step 2: If NOT mdOnboard with appointment, go to visit format
      // (mdOnboard skip is handled by a separate useEffect below)
      if (holdBooking) {
        setTimeout(() => {
          onRequestAppointment?.(null)
          if (pharmacy) {
            setStack(prev => [...prev, { phase: 'pharmacy', data: pharmacy }])
            if (agreementSigned) {
              setStack(prev => [...prev, { phase: 'agreement', data: { signed: true } }])
              setActivePhase('review')
            } else {
              setActivePhase('agreement')
            }
          } else {
            setActivePhase('pharmacy')
          }
        }, 3200)
      } else if (!mdOnboardMode || !existingAppointment?.date) {
        setTimeout(() => {
          setActivePhase('appointment-group')
          setApptStep('visit-format')
        }, 3200)
      }
    }
  }, [])
  
  // v191.8: MD onboard skip — watches existingAppointment reactively.
  // When appointment data arrives (may be after mount due to state batching),
  // skip appointment phase and go straight to pharmacy.
  const hasSkippedRef = useRef(false)
  useEffect(() => {
    if (hasSkippedRef.current) return
    if (!mdOnboardMode || !existingAppointment?.date) return
    // Only skip if we haven't progressed past appointment yet
    if (activePhase !== 'intro' && activePhase !== 'appointment-group') return
    
    hasSkippedRef.current = true
    console.log('[ShareConversation] MD onboard — skipping appointment phase, going to pharmacy')
    
    // Use a short delay to let the intro animation play first
    const delay = activePhase === 'intro' ? 2000 : 0
    setTimeout(() => {
      // Stack the intro if not already there
      setStack(prev => {
        const hasIntro = prev.some(s => s.phase === 'intro')
        const base = hasIntro ? prev.filter(s => s.phase !== 'appointment-group') : [{ phase: 'intro' }]
        return [...base, { phase: 'appointment-group', data: { 
          type: existingAppointment.type || appointmentType, 
          date: existingAppointment.date, 
          time: existingAppointment.time 
        }}]
      })
      onRequestAppointment?.({ 
        type: existingAppointment.type || appointmentType, 
        date: existingAppointment.date, 
        time: existingAppointment.time 
      })
      if (pharmacy) {
        setTimeout(() => {
          setStack(prev => [...prev, { phase: 'pharmacy', data: pharmacy }])
          if (agreementSigned) {
            setStack(prev => [...prev, { phase: 'agreement', data: { signed: true } }])
            setActivePhase('review')
          } else {
            setActivePhase('agreement')
          }
        }, 100)
      } else {
        setActivePhase('pharmacy')
      }
    }, delay)
  }, [existingAppointment, activePhase, mdOnboardMode])

  // P1 round 6 (A1): the slot was taken between the pick and the send. Back to the time
  //   picker, with the visit type and the DATE kept and only the taken time cleared; the
  //   appointment card and everything stacked after it are unstacked, so the pharmacy and
  //   agreement cards re-stack after the new time is confirmed. 0 is the mount: no jump.
  useEffect(() => {
    if (!slotConflictSeq) return
    setSelectedTime(null)
    setStack((prev) => {
      const idx = prev.findIndex((s) => s.phase === 'appointment-group')
      return idx >= 0 ? prev.slice(0, idx) : prev
    })
    setActivePhase('appointment-group')
    setApptStep('date-time')
  }, [slotConflictSeq])

  // ─── Navigation ───
  const stackPhase = useCallback((phase, data = {}) => {
    setStack(prev => [...prev, { phase, data }])
  }, [])

  const goBack = useCallback(() => {
    // Within appointment transition group
    if (activePhase === 'appointment-group') {
      const idx = apptStepOrder.indexOf(apptStep)
      if (idx > 0) {
        setApptStep(apptStepOrder[idx - 1])
        return
      }
      // At first appointment step — can't go back past intro
      return
    }
    
    // Pop last stacked card and return to its phase for editing
    if (stack.length > 1) {
      const prev = stack[stack.length - 1]
      setStack(s => s.slice(0, -1))
      
      if (prev.phase === 'appointment-group') {
        setActivePhase('appointment-group')
        setApptStep('visit-format')
      } else if (prev.phase === 'pharmacy') {
        setActivePhase('pharmacy')
      } else if (prev.phase === 'agreement') {
        setActivePhase('agreement')
      } else if (prev.phase === 'review') {
        setActivePhase('review')
      }
    }
  }, [activePhase, apptStep, stack, authRequired])

  const editStackedItem = useCallback((phase) => {
    // Remove this item and everything after it from stack
    const idx = stack.findIndex(s => s.phase === phase)
    if (idx >= 0) {
      setStack(prev => prev.slice(0, idx))
      if (phase === 'appointment-group') {
        setActivePhase('appointment-group')
        setApptStep('visit-format')
      } else {
        setActivePhase(phase)
      }
    }
  }, [stack])

  // ─── Appointment group handlers ───
  // v177.0.0: Scheduling is mandatory — no yes/no step, go straight to format selection

  const handleVisitFormat = (type) => {
    setAppointmentType(type)
    setApptStep(authRequired ? 'authorization' : 'date-time')
  }

  // v235.1.0 — auth step → continue to scheduling. Capture (when added) is
  // saved to the cassette by PatientUploadSection; defer leaves it pending and
  // the check-in gate collects it. Booking is never blocked either way.
  const handleAuthDone = () => setApptStep('date-time')
  const handleAuthDefer = () => setApptStep('date-time')

  const handleDateTimeConfirm = () => {
    const apptData = { type: appointmentType, date: selectedDate, time: selectedTime }
    onRequestAppointment?.(apptData)
    stackPhase('appointment-group', apptData)
    setActivePhase('pharmacy')
  }

  // ─── Pharmacy handler ───
  const handlePharmacySave = (data) => {
    // If pharmacy changed from the profile-saved one, ask user what to do
    const isDifferent = savedPharmacy && data && data.name !== savedPharmacy.name
    if (isDifferent) {
      setPendingPharmacy(data)
      return // wait for choice
    }
    // v242.32.0 (FB5): no pharmacy on file yet → adopt this selection as the
    // default (persist it). Same-as-saved → just advance.
    const noPriorPharmacy = !savedPharmacy && !!data && !data.skipped
    commitPharmacy(data, noPriorPharmacy)
  }

  const commitPharmacy = (data, saveToProfile) => {
    setPharmacy(data)
    onSelectPharmacy?.(data)
    stackPhase('pharmacy', data || { skipped: true })
    setActivePhase('agreement')
    setPendingPharmacy(null)

    if (saveToProfile && data) {
      updateProfile({ preferredPharmacy: data })
      syncSection('preferredPharmacy', data).then(r => {
        if (r.success) console.log('[Pharmacy] Saved to profile')
        else console.warn('[Pharmacy] Profile save failed, has error:', !!r.error)
      })
    }
  }

  // v145.12.0: Auto-advance past pharmacy if already pre-populated from profile
  useEffect(() => {
    if (activePhase === 'pharmacy' && pharmacy && !pharmacy.skipped) {
      onSelectPharmacy?.(pharmacy)
      stackPhase('pharmacy', pharmacy)
      setActivePhase('agreement')
    }
  }, [activePhase])

  const handlePharmacySkip = () => {
    onSelectPharmacy?.(null)
    stackPhase('pharmacy', { skipped: true })
    setActivePhase('agreement')
  }

  // ─── Agreement handler ───
  useEffect(() => {
    if (agreementSigned && activePhase === 'agreement') {
      setTimeout(() => {
        stackPhase('agreement', { signed: true })
        setActivePhase('review')
      }, 600)
    }
  }, [agreementSigned, activePhase])

  // ─── Chat messages ───
  const getChatMessage = () => {
    if (activePhase === 'intro') {
      return mdOnboardMode
        ? `${drName} has set up a case for you. Let's confirm a few details together.`
        : t('shareConv.getReadyToSend', { doctor: drName })
    }
    if (activePhase === 'appointment-group') {
      if (apptStep === 'visit-format') return t('shareConv.whatTypeVisit')
      if (apptStep === 'authorization') return t('shareConv.authStepIntro', "Your plan requires an authorization or referral for this visit. Add it now and I'll read the details automatically — so everything's ready by the time you're seen.")
      if (apptStep === 'date-time') return t('shareConv.pickDateTime', { doctor: drName })
    }
    if (activePhase === 'pharmacy') return 'If medication is prescribed, where should we send it? You can search any pharmacy or enter one manually.'
    if (activePhase === 'agreement') return t('shareConv.signAgreement')
    if (activePhase === 'review') return t('shareConv.reviewAndSend', { doctor: drName })
    return ''
  }

  // ─── Format helpers ───
  const formatTime = (time) => {
    if (!time) return ''
    const hour = parseInt(time.split(':')[0])
    const mins = time.split(':')[1] || '00'
    return hour >= 12 ? `${hour === 12 ? 12 : hour - 12}:${mins} PM` : `${hour}:${mins} AM`
  }

  // v222.4.8 — defensive: practice_address may be a JSONB dict / JSON-string
  const doctorAddress = [
    formatAddress(doctor.practice_address),
    [doctor.practice_city, doctor.practice_state].filter(Boolean).join(', '),
    doctor.practice_zip,
  ].filter(Boolean).join(', ')

  const doctorPhone = doctor.phone || doctor.practice_phone

  // ★★★ D-g / D-b (Alex; round 2 of P1, A2). ONLY WHAT /slots RETURNED. This used to fall
  //   back to today + 14 days (no Sundays) and a static 9:00–16:30 list whenever the
  //   physician's calendar was empty, so a patient "booked" a time with a physician whose
  //   online scheduling was off, or on with no days. Scheduling is mandatory here, and D-g
  //   makes a physician without real hours unselectable in the share flow, so there is
  //   nothing to fall back FOR: no slots means NoSlotsCard (by slotsReason) and no
  //   calendar. Times are 'HH:MM' and sorted by minutes (utils/bookableSlots.js); the old
  //   string sort put 10:00 before 9:00.
  // ★★★ D527 (Dr. Rami 2026-10-23, time request; handoff s116): REVERSES the paragraph above for a physician
  //   with NO usable hours ('scheduling-off' / 'no-hours') ONLY. Those get the request grid (utils/bookableSlots
  //   requestGridSlots: 14 days, weekdays only, 9:00-16:30 every 30 min) under the same same-day lead rule, with the
  //   approved notice above the calendar. The patient REQUESTS a time (required: there is no skip); the API stores
  //   it as timeRequestOnly and the physician confirms or changes it. 'none-open' and 'fetch-failed' keep NoSlotsCard.
  const timeRequestMode = !loadingSlots && isTimeRequestReason(slotsReason) && datesOf(availableSlots).length === 0
  const calendarSlots = timeRequestMode ? requestGridSlots() : availableSlots
  const dateOptions = (() => {
    const now = new Date()
    const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
    // P1 round 3 (H4): a date is offered only if a time survives the same-day lead
    //   buffer, the rule timeSlots applies below; today with nothing left is not a date.
    return datesOf(calendarSlots).filter(d => d >= today &&
      timesAfterSameDayLead(timesForDate(calendarSlots, d), d, appointmentType).length > 0)
  })()

  // v225.10.0 (#6): same-day lead-time buffer — patients can't book a slot that's
  // already passed OR too soon to realistically make it (in person >= 60 min, virtual
  // >= 30 min; appointmentType 'video' = virtual). The rule lives in
  // utils/bookableSlots.js timesAfterSameDayLead (P1 round 3, H4), shared with
  // dateOptions above. Recomputed every render, so toggling the visit type re-applies it.
  const timeSlots = timesAfterSameDayLead(timesForDate(calendarSlots, selectedDate), selectedDate, appointmentType)

  // ★★ D7 (PROD-639, PD7-1): WHERE THE VISIT IS. slotPlaces is GET /slots' `locations` +
  //   `slotLocations` (utils/bookableSlots.js slotPlacesOf), null for an API without them,
  //   and then nothing below renders: the picker is what it was before D7. A video visit has
  //   no place. The location NAME shows only when the physician has more than one location.
  const placeOf = (date, time, type) => slotPlace(slotPlaces, date, time, ['video', 'telehealth', 'virtual'].includes(type))
  const timeSubLabels = slotPlaceNames(slotPlaces, selectedDate, timeSlots, appointmentType === 'video')

  // ═══════════════════════════════════════
  // STACKED CARD COMPONENTS
  // ═══════════════════════════════════════

  const StackedCard = ({ icon: Icon, title, iconColor = 'text-green-600', borderColor = 'border-green-200', bgColor = 'bg-green-50', onEdit, children }) => (
    <div className="bg-white rounded-2xl border-[1.5px] overflow-hidden" style={{ borderColor: borderColor.startsWith('#') ? borderColor : undefined, animation: 'fadeInUp 0.3s ease' }}>
      <div className={`flex items-center gap-2 px-3.5 py-2 border-l-[3px] border-l-green-500 ${bgColor}`}>
        <Icon size={14} className={iconColor} />
        <span className="text-[11px] font-bold uppercase tracking-[0.5px] text-green-700">{title}</span>
        <div className="ml-auto flex items-center gap-1.5">
          <CheckCircle size={14} className="text-green-500" />
          {onEdit && (
            <button onClick={onEdit} className="p-1 text-green-500 active:text-green-700">
              <Edit3 size={12} />
            </button>
          )}
        </div>
      </div>
      <div className="px-3.5 py-2.5">{children}</div>
    </div>
  )

  const renderStackedItem = (item, idx) => {
    const { phase, data } = item

    if (phase === 'intro') {
      // Persist the selected doctor info card under the intro bubble
      return (
        <div key={idx}
          className="bg-white rounded-2xl border-[1.5px] border-[#A7D9FC]/40 overflow-hidden cursor-pointer active:scale-[0.98] transition-transform"
          style={{ animation: 'fadeInUp 0.3s ease' }}
          onClick={() => onViewProfile?.(doctor)}
        >
          <div className="flex items-center gap-3 p-3.5 pb-2">
            <div className="w-11 h-11 bg-[#E9F8FF] rounded-full flex items-center justify-center flex-shrink-0 border-2 border-[#A7D9FC]/30">
              {doctor.profile_photo ? (
                <img src={doctor.profile_photo} alt="" className="w-full h-full rounded-full object-cover" />
              ) : (
                <span className="text-[#46A8F9] font-bold text-[13px]">
                  {doctor.first_name?.[0]}{doctor.last_name?.[0]}
                </span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <h3 className="font-bold text-[14px] truncate" style={{ color: NAVY }}>
                  {drFullName}
                </h3>
                {doctor.board_certified && (
                  <span className="text-[8px] font-bold px-1.5 py-0.5 rounded bg-green-100 text-green-700 flex-shrink-0">{t('share.boardCertShort')}</span>
                )}
              </div>
              <p className="text-[#46A8F9] text-[12px] font-medium">{((doctor.specialties && doctor.specialties[0]) || '')}</p>
              {(doctor.practice_name || doctor.practice_city) && (
                <p className="text-[11px] text-gray-400 mt-0.5 truncate">
                  {doctor.practice_name ? `${doctor.practice_name}` : ''}
                  {doctor.practice_name && doctor.practice_city ? ' · ' : ''}
                  {doctor.practice_city}{doctor.practice_state ? `, ${doctor.practice_state}` : ''}
                </p>
              )}
            </div>
            <button 
              onClick={(e) => { e.stopPropagation(); onViewProfile?.(doctor) }}
              className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 active:scale-95 transition-transform"
              style={{ background: '#E9F8FF', border: '1.5px solid #A7D9FC' }}
            >
              <User size={16} style={{ color: '#46A8F9' }} />
            </button>
          </div>
          {/* View Profile — real tap target (v217.1.2 fix: was a dead <span>,
              now a real <button> so taps reliably open the sheet on iOS Safari
              where event bubbling through the outer card click can be flaky). */}
          <button
            onClick={(e) => { e.stopPropagation(); onViewProfile?.(doctor) }}
            className="w-full px-3.5 pb-2.5 pt-1.5 border-t border-[#F1F5F9] mx-2 mb-1 flex items-center gap-1.5 text-[11px] font-semibold text-[#46A8F9] active:bg-[#F1F5F9] transition-colors text-left"
          >
            <User size={12} /> {t('share.viewProfile')}
          </button>
        </div>
      )
    }

    if (phase === 'appointment-group') {
      return (
        <StackedCard key={idx} icon={Calendar} title={t('shareConv.appointment')} onEdit={() => editStackedItem('appointment-group')}>
          <div className="flex items-center gap-2 mb-1">
            {data?.type === 'video' ? <Video size={14} className="text-[#46A8F9]" /> : <MapPin size={14} className="text-[#46A8F9]" />}
            <p className="text-[13px] font-semibold" style={{ color: NAVY }}>
              {data?.type === 'video' ? t('shareConv.virtualVisit') : t('shareConv.inPersonVisit')}
            </p>
          </div>
          <p className="text-[12px] text-gray-600 ml-[22px]">
            {t('shareConv.dateAtTime', { date: data?.date ? formatDate(data.date, { weekday: 'long', month: 'long', day: 'numeric' }) : '', time: formatTime(data?.time) })}
          </p>
          <SlotPlaceLine place={placeOf(data?.date, data?.time, data?.type)} className="ml-[22px] mt-0.5" />
        </StackedCard>
      )
    }

    if (phase === 'pharmacy') {
      if (data?.skipped) {
        return (
          <StackedCard key={idx} icon={Pill} title={t('shareConv.pharmacy')} onEdit={() => editStackedItem('pharmacy')}>
            <p className="text-[12px] text-gray-500 italic">{t('shareConv.notSelectedYouCanAdd')}</p>
          </StackedCard>
        )
      }
      return (
        <StackedCard key={idx} icon={Pill} title={t('shareConv.pharmacy')} onEdit={() => editStackedItem('pharmacy')}>
          <p className="text-[13px] font-semibold" style={{ color: NAVY }}>{data?.name}</p>
          {data?.address && <p className="text-[11px] text-gray-500">{data.address}</p>}
          {data?.phone && <p className="text-[11px] text-[#46A8F9]">{data.phone}</p>}
        </StackedCard>
      )
    }

    if (phase === 'agreement') {
      return (
        <StackedCard key={idx} icon={FileText} title={t('shareConv.agreement')} onEdit={null}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CheckCircle size={14} className="text-green-500" />
              <p className="text-[12px] text-green-700 font-medium">{t('shareConv.patientCareAgreementSigned')}</p>
            </div>
            <a href="#" onClick={(e) => { e.preventDefault(); setShowAgreementPreview(true) }}
              className="flex items-center gap-1 text-[11px] font-semibold text-[#46A8F9] active:opacity-70"
            >
              <Eye size={12} /> {t('shareConv.viewPdf', 'View PDF')}
            </a>
          </div>
        </StackedCard>
      )
    }

    return null
  }

  // ═══════════════════════════════════════
  // ACTIVE PROMPT CONTENT
  // ═══════════════════════════════════════

  const renderActiveContent = () => {
    // ── Appointment Group (transitions within) ──
    if (activePhase === 'appointment-group') {
      if (apptStep === 'visit-format') {
        return (
          <div className="space-y-2">
            {authRequired && (
              <div className="flex items-center gap-2 rounded-full px-3 py-2 mb-1" style={{ border: '1.5px solid #FCD9A3', color: '#B45309', background: '#fff', width: 'fit-content' }}>
                <AlertTriangle size={14} style={{ color: '#B45309' }} />
                <span className="text-[12px] font-bold">{t('shareConv.authRequiredPill', 'Authorization required')}</span>
              </div>
            )}
            {/* ★ s101 (Dr. Rami, 2026-10-12, Alex's Build 65 x_14): "we favor the boxes instead of the
                rows" — the in-office / virtual choice is drawn as the follow-up booking's side-by-side
                boxes (ScheduleFollowupScreen), so both booking screens look the same. One tap still
                chooses and moves on (handleVisitFormat); the order is in-office first, as there. */}
            <div data-visit-format-boxes className={`grid ${offersVideo && offersInPerson ? 'grid-cols-2' : 'grid-cols-1'} gap-3`}>
            {offersInPerson && (
            <button onClick={() => handleVisitFormat('in-person')}
              className="p-4 rounded-xl border-2 border-gray-200 bg-white flex flex-col items-center text-center gap-1.5 active:border-[#46A8F9] active:bg-[#E9F8FF] transition-colors"
              style={{ color: NAVY, animation: 'fadeInUpSlow 0.4s ease 0s both' }}
            >
              <div className="w-11 h-11 rounded-xl bg-[#E9F8FF] flex items-center justify-center">
                <MapPin size={22} className="text-[#46A8F9]" />
              </div>
              <p className="text-[13.5px] font-bold">{t('shareConv.inpersonVisit')}</p>
              <p className="text-[11px] text-gray-400 font-normal leading-snug">{doctorAddress || 'At doctor\'s office'}</p>
              {isSelfPayPatient && spInOfficeOk && (
                <span className="text-[14px] font-extrabold" style={{ color: '#15803D' }}>${Number(_spInOffice)}</span>
              )}
            </button>
            )}
            {offersVideo && (
            <button onClick={() => handleVisitFormat('video')}
              className="p-4 rounded-xl border-2 border-gray-200 bg-white flex flex-col items-center text-center gap-1.5 active:border-[#46A8F9] active:bg-[#E9F8FF] transition-colors"
              style={{ color: NAVY, animation: 'fadeInUpSlow 0.4s ease 0.12s both' }}
            >
              <div className="w-11 h-11 rounded-xl bg-[#E9F8FF] flex items-center justify-center">
                <Video size={22} className="text-[#46A8F9]" />
              </div>
              <p className="text-[13.5px] font-bold">{t('shareConv.virtualVisit')}</p>
              <p className="text-[11px] text-gray-400 font-normal leading-snug">{t('shareConv.videoCallFromHome')}</p>
              {isSelfPayPatient && spVirtualOk && (
                <span className="text-[14px] font-extrabold" style={{ color: '#15803D' }}>${Number(_spVirtual)}</span>
              )}
            </button>
            )}
            </div>
          </div>
        )
      }

      if (apptStep === 'authorization') {
        return (
          <div className="space-y-3" style={{ animation: 'fadeInUp 0.3s ease' }}>
            <PatientUploadSection
              currentCase={currentCase}
              isHMO={true}
              hasUploaded={false}
              onUploaded={handleAuthDone}
              bookingMode={true}
              onDefer={handleAuthDefer}
            />
          </div>
        )
      }

      if (apptStep === 'date-time') {
        // D-g (round 2, A2): while the calendar loads there is nothing to pick yet, and
        // with no real date there is no calendar at all — the card says why.
        if (loadingSlots) {
          return (
            <div className="py-6 flex justify-center" style={{ animation: 'fadeInUp 0.3s ease' }}>
              <OrellaSpinner size={18} tone="inherit" className="text-gray-300" />
            </div>
          )
        }
        if (dateOptions.length === 0) {
          return (
            <div className="space-y-3" style={{ animation: 'fadeInUp 0.3s ease' }}>
              <NoSlotsCard reason={slotsReason || 'none-open'} phone={doctorPhone} onRetry={onRetrySlots} />
            </div>
          )
        }
        // v163: Build slot count per date for calendar display
        const slotCountByDate = {}
        for (const entry of normalizeSlots(calendarSlots)) {
          slotCountByDate[entry.date] = entry.times.length
        }
        return (
          <div className="space-y-3" style={{ animation: 'fadeInUp 0.3s ease' }}>
            {timeRequestMode && (
              <div data-time-request-notice className="bg-[#FFF8EE] border border-[#F5C58A] rounded-xl p-3">
                <p className="text-[12px] leading-snug" style={{ color: '#5A3A0B' }}>{t('shareConv.timeRequestNotice')}</p>
              </div>
            )}
            <AppointmentCalendar
              availableDates={dateOptions}
              timeSlots={selectedDate ? timeSlots : []}
              selectedDate={selectedDate}
              selectedTime={selectedTime}
              onSelectDate={(d) => { setSelectedDate(d); setSelectedTime(null) }}
              onSelectTime={setSelectedTime}
              slotCountByDate={slotCountByDate}
              timeSubLabels={selectedDate ? timeSubLabels : null}
            />

            {/* Confirm */}
            {selectedDate && selectedTime && (
              <div className="pt-1">
                <div className="bg-[#E9F8FF] rounded-xl p-3 mb-2 flex items-center gap-2.5">
                  {appointmentType === 'video' ? <Video size={16} className="text-[#46A8F9]" /> : <MapPin size={16} className="text-[#46A8F9]" />}
                  <p className="text-[12px] text-[#15354F]">
                    <span className="font-bold">{t('shareConv.requesting')}</span>{' '}
                    {t('shareConv.dateAtTime', { date: formatDate(selectedDate, { weekday: 'long', month: 'long', day: 'numeric' }), time: formatTime(selectedTime) })} ({appointmentType === 'video' ? t('shareConv.virtual') : t('shareConv.inPerson')})
                  </p>
                </div>
                <SlotPlaceLine place={placeOf(selectedDate, selectedTime, appointmentType)} className="mb-2 px-1" />
                <button onClick={handleDateTimeConfirm}
                  className="w-full py-3 rounded-xl font-bold text-[13px] text-white breathe-blue"
                  style={{ backgroundColor: BLUE }}
                >
                  {t('shareConv.confirmDateTime')}
                </button>
              </div>
            )}
          </div>
        )
      }
    }

    // ── Pharmacy ──
    if (activePhase === 'pharmacy') {
      // Show save-choice when user picked a different pharmacy
      if (pendingPharmacy) {
        return (
          <div className="space-y-3" style={{ animation: 'fadeInUp 0.3s ease' }}>
            {/* Preview of selected pharmacy */}
            <div className="bg-white rounded-2xl border-[1.5px] border-[#A7D9FC] overflow-hidden">
              <div className="flex items-center gap-2 px-3.5 py-2.5 border-l-[3px] border-l-[#46A8F9] bg-[#E9F8FF]">
                <Pill size={15} className="text-[#46A8F9]" />
                <h4 className="text-[12px] font-bold uppercase tracking-[0.5px]" style={{ color: NAVY }}>{t('shareConv.newPharmacy')}</h4>
              </div>
              <div className="px-3.5 py-3">
                <p className="text-[13px] font-bold" style={{ color: NAVY }}>{pendingPharmacy.name}</p>
                {pendingPharmacy.address && (
                  <p className="text-[11px] text-gray-500 mt-0.5">{pendingPharmacy.address}</p>
                )}
              </div>
            </div>

            {/* Choice buttons */}
            <div className="space-y-2">
              <button
                onClick={() => commitPharmacy(pendingPharmacy, false)}
                className="w-full py-3 rounded-xl font-semibold text-[13px] border-[1.5px] border-gray-200 text-gray-600 active:bg-gray-50 transition-colors"
              >
                {t('shareConv.useForThisCaseOnly')}
              </button>
              <button
                onClick={() => commitPharmacy(pendingPharmacy, true)}
                className="w-full py-3 rounded-xl font-bold text-[13px] text-white flex items-center justify-center gap-2 active:opacity-90 transition-colors"
                style={{ backgroundColor: BLUE }}
              >
                <Save size={14} />
                {t('shareConv.saveAsDefaultPharmacy', 'Save as my default pharmacy')}
              </button>
            </div>
          </div>
        )
      }

      return (
        <div className="space-y-2" style={{ animation: 'fadeInUp 0.3s ease' }}>
          <PharmacyLookup
            savedPharmacy={pharmacy}
            allowSkip={false}
            onSave={(data) => {
              if (data) handlePharmacySave(data)
              else handlePharmacySkip()
            }}
          />
        </div>
      )
    }

    // ── Agreement ──
    if (activePhase === 'agreement') {
      return (
        <div className="bg-white rounded-2xl border-[1.5px] border-gray-200 overflow-hidden" style={{ animation: 'fadeInUpSlow 0.45s ease both' }}>
          <div className="p-4">
            {!agreementSigned ? (
              <>
                <p className="text-[12px] text-gray-500 mb-4 leading-relaxed">
                  {t('shareConv.agreementBody', 'This agreement outlines the terms of care and your rights as a patient. Please review and sign to continue.')}
                </p>
                <div className="flex gap-2">
                  <a href="#" onClick={(e) => { e.preventDefault(); setShowAgreementPreview(true) }}
                    className="flex-1 py-3 rounded-xl bg-gray-50 text-[12px] font-semibold text-gray-600 flex items-center justify-center gap-2 active:bg-gray-100"
                  >
                    <Eye size={16} />
                    {t('shareConv.viewPdf', 'View PDF')}
                  </a>
                  <button onClick={onSignAgreement}
                    className="flex-1 py-3 rounded-xl text-[12px] font-bold text-white flex items-center justify-center gap-2 breathe-blue"
                    style={{ backgroundColor: BLUE }}
                  >
                    <PenTool size={16} />
                    {t('share.signAgreement', 'Sign Agreement')}
                  </button>
                </div>
              </>
            ) : (
              <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-xl">
                <CheckCircle className="text-green-500" size={24} />
                <div>
                  <p className="font-medium text-green-800 text-[13px]">{t('shareConv.agreementSigned')}</p>
                  <p className="text-[11px] text-green-600">{t('shareConv.signedJustNow')}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )
    }

    // ── Review & Send ──
    if (activePhase === 'review') {
      return (
        <div className="space-y-3" style={{ animation: 'fadeInUp 0.3s ease' }}>
          <div className="bg-white rounded-2xl border-[1.5px] border-gray-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100">
              <p className="text-[11px] font-bold uppercase tracking-widest" style={{ color: NAVY }}>{t('shareConv.reviewBeforeSending')}</p>
            </div>

            {/* Doctor */}
            <div className="px-4 py-3 border-b border-gray-50">
              <div className="flex items-center gap-2.5 mb-1.5">
                <User size={14} className="text-[#46A8F9]" />
                <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">{t('shareConv.doctor')}</span>
              </div>
              <p className="text-[13px] font-bold" style={{ color: NAVY }}>{drFullName}</p>
              <p className="text-[11px] text-gray-500">{((doctor.specialties && doctor.specialties[0]) || '')}{doctor.practice_name ? ` · ${doctor.practice_name}` : ''}</p>
              {doctorAddress && <p className="text-[11px] text-gray-400 mt-0.5">{doctorAddress}</p>}
              {doctorPhone && <p className="text-[11px] text-[#46A8F9] mt-0.5">{doctorPhone}</p>}
            </div>

            {/* Appointment */}
            <div className="px-4 py-3 border-b border-gray-50">
              <div className="flex items-center justify-between mb-1.5">
                <div className="flex items-center gap-2.5">
                  <Calendar size={14} className="text-[#46A8F9]" />
                  <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">{t('onboard.appointment', 'Appointment')}</span>
                </div>
                {!holdBooking && <button onClick={() => editStackedItem('appointment-group')} className="text-[10px] font-semibold text-[#46A8F9] active:opacity-70">{t('shareConv.edit')}</button>}
              </div>
              {holdBooking ? (
                <p className="text-[12px] text-gray-600" data-testid="share-booking-held">
                  {t('referral.bookNote', { defaultValue: '{{doctor}} receives your case now. Booking with {{doctor}} opens once the referral is on file.', doctor: drFullName })}
                </p>
              ) : wantsAppointment && selectedDate && selectedTime ? (
                <>
                  <p className="text-[13px] font-semibold flex items-center gap-1.5" style={{ color: NAVY }}>
                    {appointmentType === 'video' ? <Video size={14} className="text-[#46A8F9]" /> : <MapPin size={14} className="text-[#46A8F9]" />}
                    {appointmentType === 'video' ? t('shareConv.virtualVisit') : t('shareConv.inPersonVisit')}
                  </p>
                  <p className="text-[12px] text-gray-600">
                    {t('shareConv.dateAtTime', { date: formatDate(selectedDate, { weekday: 'long', month: 'long', day: 'numeric' }), time: formatTime(selectedTime) })}
                  </p>
                  <SlotPlaceLine place={placeOf(selectedDate, selectedTime, appointmentType)} className="mt-0.5" />
                </>
              ) : (
                <p className="text-[12px] text-gray-500 italic">{t('shareConv.notRequested', 'Not requested —')} {drName} will coordinate</p>
              )}
            </div>

            {/* Pharmacy */}
            <div className="px-4 py-3 border-b border-gray-50">
              <div className="flex items-center justify-between mb-1.5">
                <div className="flex items-center gap-2.5">
                  <Pill size={14} className="text-green-500" />
                  <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">{t('onboard.pharmacy', 'Pharmacy')}</span>
                </div>
                <button onClick={() => editStackedItem('pharmacy')} className="text-[10px] font-semibold text-[#46A8F9] active:opacity-70">{t('shareConv.edit')}</button>
              </div>
              {pharmacy && !pharmacy.skipped ? (
                <>
                  <p className="text-[13px] font-semibold" style={{ color: NAVY }}>{pharmacy.name}</p>
                  {pharmacy.address && <p className="text-[11px] text-gray-500">{pharmacy.address}</p>}
                  {pharmacy.phone && <p className="text-[11px] text-[#46A8F9]">{pharmacy.phone}</p>}
                </>
              ) : (
                <p className="text-[12px] text-gray-500 italic">{t('shareConv.notSelectedYouCanAdd')}</p>
              )}
            </div>

            {/* Agreement */}
            <div className="px-4 py-3">
              <div className="flex items-center justify-between mb-1.5">
                <div className="flex items-center gap-2.5">
                  <FileText size={14} className="text-green-500" />
                  <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">{t('onboard.agreement', 'Agreement')}</span>
                </div>
                <a href="#" onClick={(e) => { e.preventDefault(); setShowAgreementPreview(true) }}
                  className="text-[10px] font-semibold text-[#46A8F9] active:opacity-70 flex items-center gap-1"
                >
                  <Eye size={10} /> {t('shareConv.viewPdf', 'View PDF')}
                </a>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle size={14} className="text-green-500" />
                <p className="text-[12px] text-green-700 font-medium">{t('shareConv.patientCareAgreementSigned')}</p>
              </div>
            </div>
          </div>

          {/* P1 round 6 (A1): the refusal of the last send, above the Send it answers */}
          {error && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl" data-share-send-error>
              <p className="text-sm text-amber-700">{error}</p>
            </div>
          )}

          {/* Send Button */}
          <button
            onClick={onSend}
            disabled={sendingToDoctor}
            className={`w-full py-4 rounded-2xl font-bold text-[15px] text-white flex items-center justify-center gap-2 shadow-lg shadow-blue-200 active:opacity-90 transition-opacity disabled:opacity-50 ${!sendingToDoctor ? 'breathe-navy' : ''}`}
            style={{ backgroundColor: NAVY }}
          >
            {sendingToDoctor ? (
              <><OrellaSpinner size={20} tone="inherit" /> {t('common.sending', 'Sending...')}</>
            ) : (
              <><Send size={20} /> {t('shareConv.sendTo', 'Send to')} {drName}</>
            )}
          </button>

          <button onClick={onBack} className="w-full text-center text-[12px] text-gray-400 font-medium py-1">
            {t('shareConv.chooseDifferentDoctor', 'Choose Different Doctor')}
          </button>
        </div>
      )
    }

    return null
  }

  // Can we go back from active phase?
  const canGoBack = () => {
    if (activePhase === 'appointment-group') {
      return apptStepOrder.indexOf(apptStep) > 0
    }
    return stack.length > 1 // Always can go back to previous stacked phase
  }

  // ═══════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════

  return (
    <div className="space-y-3">
      <ChatKeyframes />
      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeInUpSlow {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {/* ── Stacked completed items ── */}
      {stack.map((item, idx) => (
        <div key={`${item.phase}-${idx}`}>
          {/* Show intro chat bubble for the first item */}
          {item.phase === 'intro' && (
            <OrellaChatBubble
              message={mdOnboardMode
                ? `${drName} has set up a case for you. Let's confirm a few details together.`
                : t('shareConv.getReadyToSend', { doctor: drName })
              }
              animationKey={`share-intro-${doctor.id}`}
              onComplete={() => {}}
            />
          )}
          {/* Render stacked card with staggered delay based on position */}
          <div className="px-4" style={{ animation: `fadeInUpSlow 0.45s ease ${idx * 0.1}s both` }}>
            {renderStackedItem(item, idx)}
          </div>
        </div>
      ))}

      {/* ── Active chat bubble + content ── */}
      {activePhase !== 'intro' && (
        <>
          {/* TEST 3.15: where the profile sheet's CTA brings the patient back to */}
          <div data-share-active-step />
          {/* Go back button */}
          {canGoBack() && (
            <div className="px-4">
              <button onClick={goBack} className="flex items-center gap-1 text-[11px] text-gray-400 font-medium mb-1 active:text-gray-600">
                <ChevronLeft size={14} /> {t('startCase.goBack')}
              </button>
            </div>
          )}

          <OrellaChatBubble
            message={getChatMessage()}
            animationKey={`share-${activePhase}-${apptStep}-${doctor.id}`}
            onComplete={() => setShowContent(true)}
          />

          {showContent && (
            <div className="px-4" style={{ animation: 'fadeInUpSlow 0.5s ease 0.15s both' }} ref={scrollRef}>
              {/* P1 round 6 (A1): the refusal of the last send, above the picker a slot
                  conflict returns to (the review shows it above Send instead) */}
              {error && activePhase !== 'review' && (
                <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-xl" data-share-send-error>
                  <p className="text-sm text-amber-700">{error}</p>
                </div>
              )}
              {renderActiveContent()}
            </div>
          )}
        </>
      )}
    <AgreementPreviewModal isOpen={showAgreementPreview} onClose={() => setShowAgreementPreview(false)} signatureData={agreementSigned ? (signature || '') : ''} doctor={doctor} />
    </div>
  )
}
